import React, { useCallback } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { Extrapolation, interpolate, useSharedValue } from 'react-native-reanimated';
import Carousel from 'react-native-reanimated-carousel';

import { Box } from '@/components/lib/components';

import { CasinoGame } from '../../hooks/use-casino-games';
import { useCasinoLobbyContentWidth } from '../../hooks/use-casino-lobby-content-width';
import { useStartCasinoGameUrl } from '../../hooks/use-formatted-casino-game-url';
import { CasinoLayoutProps } from '../../utils/utils';
import { CasinoGameCardBase } from '../CasinoGameCardBase';
import { LayoutHeader } from './LayoutHeader';

const FoldGameCard = ({ game }: { game: CasinoGame }) => {
    const { handleStartGame } = useStartCasinoGameUrl(game);

    return (
        <Box style={styles.foldCard}>
            <TouchableOpacity onPress={handleStartGame} activeOpacity={0.7}>
                <CasinoGameCardBase
                    game={game}
                    cardHeight={220}
                    cardWidth={220}
                    imageHeight={220}
                    customTag={game.exclusive?.effective ? { label: 'Featured' } : undefined}
                    customNameVariant="titleLarge"
                />
            </TouchableOpacity>
        </Box>
    );
};

export const FeaturedGalleryLayout = ({ data, layoutHeaderProps }: CasinoLayoutProps) => {
    const width = useCasinoLobbyContentWidth();
    const scrollProgress = useSharedValue<number>(0);

    const animationStyle = useCallback(
        (value: number) => {
            'worklet';
            const zIndex = interpolate(value, [-1, 0, 1], [10, 20, 10]);
            const inputRange = [-2, -1, 0, 1, 2];
            const outputRange = [-width, -90, 0, 90, width];

            const translateX = interpolate(value, inputRange, outputRange);

            const scale = interpolate(value, [-1, 0, 1], [0.8, 1, 0.8], Extrapolation.CLAMP);
            const opacity = interpolate(value, [-1, 0, 1], [0.8, 1, 0.8], Extrapolation.CLAMP);

            return {
                transform: [{ translateX }, { scale }],
                zIndex,
                opacity,
            };
        },
        [width]
    );

    const renderItem = useCallback(({ item }: { item: CasinoGame }) => {
        return <FoldGameCard game={item} />;
    }, []);

    if (!data || data.length === 0) {
        return null;
    }

    return (
        <Box>
            {layoutHeaderProps ? <LayoutHeader {...layoutHeaderProps} /> : null}

            <Box alignItems="center" justifyContent="center" pb="s24">
                <Carousel
                    data={data}
                    renderItem={renderItem}
                    width={width}
                    height={220}
                    loop={false}
                    autoPlay={false}
                    onProgressChange={scrollProgress}
                    customAnimation={animationStyle}
                />
            </Box>
        </Box>
    );
};

const styles = StyleSheet.create({
    foldCard: {
        alignItems: 'center',
        justifyContent: 'center',
    },
});
